import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useMount, useUnmount } from "react-use";
import MapboxDraw, { DrawCreateEvent, DrawModeChangeEvent, MapboxDrawOptions } from "@mapbox/mapbox-gl-draw";
import { featureCollection } from "@turf/helpers";
import { Feature, FeatureCollection, GeoJsonProperties, Geometry } from "geojson";
import { defer, isEmpty } from "lodash";
import { FeatureIdentifier } from "mapbox-gl";

import { DRAWING_CONSTANTS } from "fond/constants";
import defaultStyles from "fond/draw/defaultStyles";
import { HistoryContext, useHistoryContext } from "fond/history";
import { MapContext } from "fond/map/MapProvider";
import { defaultToolbarConfig, ToolbarConfig } from "fond/map/Toolbar";
import { MapboxStyleLayer } from "fond/types";

import { getDrawModes, snappingOptions, updateFeatureCollection } from "./helper";

interface DrawHandlerProps {
  modes?: {
    [modeKey: string]: MapboxDraw.DrawCustomMode<any, any>;
  };
  /**
   * The configuration of toolbar actions presented to the user.
   * @default defaultToolbarConfig
   */
  config?: ToolbarConfig;
  /**
   * The initial features that should be loaded into the drawing tool.
   */
  initialFeatures: Feature[] | null;
  /**
   * The original mapbox sources layers that contain the features to be edited / drawn.
   * This is used to determine which existing features on the map should be hidden during drawing.
   */
  sources?: FeatureIdentifier[];
  /**
   * The options to pass to the MapboxDraw instance.
   */
  options?: MapboxDrawOptions;
  /**
   * Callback function that used within the onCreate feature function to provide feature properties.
   */
  getFeatureProperties?(feature: Feature): GeoJsonProperties;
  /**
   * The default starting mode to set on load.
   * @default "simple_select"
   */
  startingMode?:
    | "simple_select"
    | "draw_line_string"
    | "draw_polygon"
    | "draw_point"
    | "static"
    | "passing_draw_polygon"
    | "passing_draw_line_string";
  /**
   * Mapbox-gl-draw styles to be added to the map.
   */
  styles?: MapboxStyleLayer[];
  /**
   * Whether to disable hover effects on features.
   * Hover handling forces a redraw of any feature that starts/stops being hovered, which can cause flickering.
   *
   * @default false
   */
  disableHoverEffects?: boolean;
}

const DrawHandler: React.FC<DrawHandlerProps> = ({
  config = defaultToolbarConfig,
  modes,
  startingMode = "simple_select",
  initialFeatures,
  options,
  sources,
  styles,
  getFeatureProperties,
  disableHoverEffects,
}: DrawHandlerProps) => {
  const { set: setHistory, state: historyState, clear: clearHistory } = useHistoryContext<FeatureCollection>(HistoryContext);
  const { map, drawControl, setIsDirty, setDrawMode, toolbar } = useContext(MapContext);
  const previousDrawControl = useRef(drawControl.current);
  const [init, setInit] = useState(false);
  const hoverFeatures = useRef(new Set<string>());

  // Store the draw control config
  useMount(() => {
    previousDrawControl.current = drawControl.current;
  });
  // restore the previous draw control for controls outside of this handler.
  useUnmount(() => {
    drawControl.current = previousDrawControl.current;
  });

  useEffect(() => {
    drawControl.current = new MapboxDraw({
      // We turn on the built in controls to allow the map to capture keyboard events (required by MapboxDraw).
      // However we hide these controls via css as we diplay our own custom controls.
      displayControlsDefault: true,
      controls: {},
      userProperties: true,
      modes: getDrawModes(modes),
      ...snappingOptions,
      ...options,
      styles: styles || defaultStyles,
      clickBuffer: 5,
    });

    toolbar.current = config;
  }, [config, map, options, drawControl, toolbar, styles, modes]);

  /**
   * Called when the history state needs updated
   */
  const updateHistory = useCallback(() => {
    if (drawControl.current?.getAll()) setHistory(drawControl.current?.getAll());
  }, [drawControl, setHistory]);

  /**
   * Updates the existing features feature-state, allowing for
   * styles to change based on edit status.
   */
  const setExistingFeatureVisibility = useCallback(
    (visibile: boolean) => {
      if (sources?.length) {
        initialFeatures?.forEach(({ id }) => {
          sources.forEach(({ source: targetSource, sourceLayer: targetSourceLayer }) => {
            map?.setFeatureState({ id: id, source: targetSource, sourceLayer: targetSourceLayer }, { isEditing: !visibile });
          });
        });
      }
    },
    [sources, initialFeatures, map]
  );

  /**
   * On create if getFeatureProperties is provided, we will update the feature properties
   * with the properties returned from the function.
   */
  const handleOnCreate = useCallback(
    (event: DrawCreateEvent) => {
      if (getFeatureProperties) {
        const newFeatures = event.features.map(
          (feature) => ({ ...feature, properties: getFeatureProperties(feature) }) as Feature<Geometry, GeoJsonProperties>
        );

        drawControl.current?.set(updateFeatureCollection(drawControl.current.getAll(), newFeatures));
      }
      updateHistory();
    },
    [drawControl, getFeatureProperties, updateHistory]
  );

  /**
   * When the maps style data changes (e.g. user changes to mono view)
   */
  const handleOnStyleChange = useCallback(() => {
    setExistingFeatureVisibility(false);
  }, [setExistingFeatureVisibility]);

  /**
   * Handle the initialization of drawing
   */
  useEffect(() => {
    // Always start in simple_select mode to initialize the drawing tools.
    setDrawMode("simple_select");

    if (initialFeatures) {
      drawControl.current?.set(featureCollection(initialFeatures));
    }

    setInit(true);
    setExistingFeatureVisibility(false);
  }, [drawControl, initialFeatures, map, setDrawMode, setExistingFeatureVisibility, startingMode]);

  useEffect(() => {
    if (init) {
      // Once the draw control has been initialized & added to the map controls we
      // switch to the starting mode defined.
      defer(() => {
        drawControl.current?.changeMode(startingMode as string);
        map?.fire("draw.modechange", { type: "draw.modechange", mode: startingMode } as DrawModeChangeEvent);
      });
    }
  }, [drawControl, init, map, setDrawMode, startingMode]);

  /**
   * Sync the drawing features with the current state of history as the user
   * clicks undo/redo.
   */
  useEffect(() => {
    if (map && drawControl.current && map.hasControl(drawControl.current)) {
      // We need to clear the existing features before setting the new ones
      // to ensure re-rendering occurs correctly.
      drawControl.current?.set(featureCollection([]));
      if (isEmpty(historyState)) {
        drawControl.current.set(featureCollection(initialFeatures || []));
      } else {
        drawControl.current.set(historyState);
      }
    }
  }, [initialFeatures, map, drawControl, historyState]);

  useEffect(() => {
    // Hide the existing features that we are about to edit
    setExistingFeatureVisibility(false);

    // Clean up when exiting drawing mode
    return () => {
      setExistingFeatureVisibility(true);
    };
  }, [drawControl, setExistingFeatureVisibility]);

  useEffect(() => {
    return () => {
      clearHistory();
      setDrawMode("no_feature");
    };
  }, [clearHistory, setDrawMode]);

  const handleOnModification = useCallback(() => {
    setIsDirty(true);
  }, [setIsDirty]);

  /**
   * Handle the mouse move event to show hover effects on features
   */
  const handleOnMouseMove = useCallback(
    (event: mapboxgl.MapMouseEvent) => {
      // Validate map still has the drawControl active
      if (!drawControl.current || !map?.hasControl(drawControl.current)) return;

      // Remove any existing hover effects
      hoverFeatures.current.forEach((id) => {
        if (id) {
          hoverFeatures.current.delete(id);
          if (!drawControl.current?.get(id)) return;
          drawControl.current.setFeatureProperty(id, DRAWING_CONSTANTS.Hover, "false");
          drawControl.current.add(drawControl.current.get(id) as Feature);
        }
      });

      // Add new hover effects
      drawControl.current?.getFeatureIdsAt(event.point).forEach((id) => {
        if (id) {
          hoverFeatures.current.add(id);
          if (!drawControl.current?.get(id)) return;
          drawControl.current.setFeatureProperty(id, DRAWING_CONSTANTS.Hover, "true");
          drawControl.current.add(drawControl.current.get(id) as Feature);
        }
      });
    },
    [drawControl, map]
  );

  // Bind events to track if the user has made any changes
  useEffect(() => {
    const dirtyEvents = ["draw.create", "draw.update", "draw.delete"];
    dirtyEvents.forEach((event) => {
      map?.on(event, handleOnModification);
    });

    return () => {
      dirtyEvents.forEach((event) => {
        map?.off(event, handleOnModification);
      });
    };
  }, [handleOnModification, map, setIsDirty]);

  useEffect(() => {
    const historyEvents = ["draw.update", "draw.combine", "draw.uncombine", "draw.delete", "draw.union"];
    historyEvents.forEach((event) => {
      map?.on(event, updateHistory);
    });

    map?.on("draw.create", handleOnCreate);
    map?.on("styledata", handleOnStyleChange);

    // 'hover' handling forces a redraw of any feature that starts/stops being hovered; this can cause flickering.
    if (!disableHoverEffects) {
      map?.on("mousemove", handleOnMouseMove);
    }

    return () => {
      historyEvents.forEach((event) => {
        map?.off(event, updateHistory);
      });

      map?.off("draw.create", handleOnCreate);
      map?.off("styledata", handleOnStyleChange);
      if (!disableHoverEffects) {
        map?.off("mousemove", handleOnMouseMove);
      }
    };
  }, [updateHistory, handleOnCreate, handleOnMouseMove, handleOnStyleChange, map, setDrawMode, disableHoverEffects]);

  return null;
};

export default DrawHandler;
